<script>
import validations from './helpers/validations';
import FormInput from 'v3/components/Form/Input.vue';
import { reactive } from 'vue';
import { useVuelidate } from '@vuelidate/core';
// NOTICE: KAPTION-INTEGRATION — Secure Assignment Mode per-team visibility control
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';

import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
    FormInput,
  },
  props: {
    onSubmit: {
      type: Function,
      default: () => {},
    },
    submitInProgress: {
      type: Boolean,
      default: false,
    },
    formData: {
      type: Object,
      default: () => {},
    },
    submitButtonText: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const formData = props.formData || {};
    const {
      description = '',
      name: title = '',
      allow_auto_assign: allowAutoAssign = true,
      // NOTICE: KAPTION-INTEGRATION — group-visibility.GROUP.2 (defaults to open)
      group_visibility: groupVisibility = 'open',
    } = formData;

    const state = reactive({
      description,
      title,
      allowAutoAssign,
      groupVisibility,
    });

    // NOTICE: KAPTION-INTEGRATION — control renders only when the feature is
    // available for the account (KAPTION_GROUP_VISIBILITY).
    const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

    const rules = validations;
    const v$ = useVuelidate(rules, state);
    return { state, v$, isKaptionFlagEnabled };
  },
  computed: {
    // NOTICE: KAPTION-INTEGRATION
    showVisibilityControl() {
      return this.isKaptionFlagEnabled('KAPTION_GROUP_VISIBILITY');
    },
  },
  methods: {
    handleSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      this.onSubmit({
        description: this.state.description,
        name: this.state.title,
        allow_auto_assign: this.state.allowAutoAssign,
        // NOTICE: KAPTION-INTEGRATION — group-visibility.API.1
        group_visibility: this.state.groupVisibility,
      });
    },
  },
};
</script>

<template>
  <div class="flex-shrink-0 w-full">
    <form class="mx-0 grid gap-4" @submit.prevent="handleSubmit">
      <FormInput
        v-model="state.title"
        name="title"
        spacing="compact"
        :label="$t('TEAMS_SETTINGS.FORM.NAME.LABEL')"
        :placeholder="$t('TEAMS_SETTINGS.FORM.NAME.PLACEHOLDER')"
        :has-error="v$.title.$error"
        :error-message="v$.title.$error ? v$.title.$errors[0].$message : ''"
        @blur="v$.title.$touch"
      />
      <FormInput
        v-model="state.description"
        name="description"
        spacing="compact"
        :label="$t('TEAMS_SETTINGS.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('TEAMS_SETTINGS.FORM.DESCRIPTION.PLACEHOLDER')"
        :has-error="v$.description.$error"
        :error-message="
          v$.description.$error ? v$.description.$errors[0].$message : ''
        "
        @blur="v$.description.$touch"
      />
      <div class="w-full flex items-center gap-2">
        <input v-model="state.allowAutoAssign" type="checkbox" :value="true" />
        <label for="conversation_creation">
          {{ $t('TEAMS_SETTINGS.FORM.AUTO_ASSIGN.LABEL') }}
        </label>
      </div>
      <!-- NOTICE: KAPTION-INTEGRATION — Secure Assignment Mode visibility (Screen 3 Step 1) -->
      <fieldset
        v-if="showVisibilityControl"
        class="w-full grid gap-2 border-0 p-0 m-0"
        data-testid="group-visibility-control"
      >
        <legend class="text-sm font-medium text-n-slate-12 p-0">
          {{ $t('TEAMS_SETTINGS.FORM.VISIBILITY.LABEL') }}
        </legend>
        <label class="flex items-start gap-2 cursor-pointer">
          <input
            v-model="state.groupVisibility"
            type="radio"
            value="restricted"
            name="group_visibility"
            class="mt-1"
          />
          <span>
            {{ $t('TEAMS_SETTINGS.FORM.VISIBILITY.RESTRICTED') }}
            <p class="text-n-slate-11 text-xs mb-0">
              {{ $t('TEAMS_SETTINGS.FORM.VISIBILITY.RESTRICTED_HELP') }}
            </p>
          </span>
        </label>
        <label class="flex items-start gap-2 cursor-pointer">
          <input
            v-model="state.groupVisibility"
            type="radio"
            value="open"
            name="group_visibility"
            class="mt-1"
          />
          <span>
            {{ $t('TEAMS_SETTINGS.FORM.VISIBILITY.OPEN') }}
            <p class="text-n-slate-11 text-xs mb-0">
              {{ $t('TEAMS_SETTINGS.FORM.VISIBILITY.OPEN_HELP') }}
            </p>
          </span>
        </label>
        <!-- NOTICE: KAPTION-INTEGRATION — group-visibility.COORDINATOR (Phase 2 selector) -->
        <label class="flex items-start gap-2 cursor-pointer">
          <input
            v-model="state.groupVisibility"
            type="radio"
            value="coordinator"
            name="group_visibility"
            class="mt-1"
          />
          <span>
            {{ $t('TEAMS_SETTINGS.FORM.VISIBILITY.COORDINATOR') }}
            <p class="text-n-slate-11 text-xs mb-0">
              {{ $t('TEAMS_SETTINGS.FORM.VISIBILITY.COORDINATOR_HELP') }}
            </p>
          </span>
        </label>
      </fieldset>
      <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full">
        <div class="w-full">
          <NextButton
            type="submit"
            :label="submitButtonText"
            :disabled="v$.title.$invalid || submitInProgress"
            :is-loading="submitInProgress"
          />
        </div>
      </div>
    </form>
  </div>
</template>
